import React, { useContext, useState } from 'react';
import { Box, Divider, Grid2, Typography } from '@mui/material';
import TextStepComponent from '../../../components/TextStepComponent';
import bridge_step1 from '../../../assets/images/defense/bridge_step1.png';
import bridge_step2 from '../../../assets/images/defense/bridge_step2.png';
import { AddCircle, ArrowDownward, Settings } from '@mui/icons-material';
import bridge_step3 from '../../../assets/images/defense/bridge_step3.png';
import bridge_step4 from '../../../assets/images/defense/bridge_step4.png';
import bridge_step5 from '../../../assets/images/defense/bridge_step5.png';
import bridge_step6 from '../../../assets/images/defense/bridge_step6.png';
import ButtonComponent from '../../../components/ButtonComponent';
import DefenseBridgeConfigurationModalComponent from './_components/DefenseBridgeConfigurationModalComponent';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useCreateDefenseBridgeAsync,
  useDeleteDefenseBridgeAsync,
  useDownloadEventTypesExcelFileAsync,
  useDownloadSourceEventTypesExcelFileAsync,
  useGetDefenseBridgeByLicenseAsync,
  useUpdateDefenseBridgeAsync,
} from '../../../services/Defense/Bridge/hooks/useMutations';
import {
  IConfigBridgeFileData,
  IDefenseBridgeProps,
} from '../../../services/Defense/Bridge/types';

const DefenseBridgePage = () => {
  const [isBridgeConfigModal, setIsBridgeConfigModal] =
    useState<boolean>(false);

  const [bridge, setBridge] = useState<IDefenseBridgeProps>();

  const { mutateAsync: mutateCreateBridgeAsync } =
    useCreateDefenseBridgeAsync();

  const { mutateAsync: mutateUpdateBridgeAsync } =
    useUpdateDefenseBridgeAsync();

  const { mutateAsync: mutateDeleteBridgeAsync } =
    useDeleteDefenseBridgeAsync();

  const { mutate: mutateGetByLicenseAsync } =
    useGetDefenseBridgeByLicenseAsync();

  const { mutateAsync: mutateDownloadEventTypes } =
    useDownloadEventTypesExcelFileAsync();

  const { mutateAsync: mutateDownloadSourceEventTypes } =
    useDownloadSourceEventTypesExcelFileAsync();

  const { showSnackbar } = useContext(AppContext);

  const steps1 = ['Configuração', 'Configuração do Sistema', 'Síntese'];

  const downloadEventTypeAsync = async () => {
    await mutateDownloadEventTypes().then((data) => buildBlob(data));
  };

  const downloadSourceEventTypeAsync = async () => {
    await mutateDownloadSourceEventTypes().then((data) => buildBlob(data));
  };

  const buildBlob = async (file: IConfigBridgeFileData) => {
    const blob = new Blob([file.data], {
      type: 'application/zip',
    });

    const link = document.createElement('a');

    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = file.fileName ?? 'AlertAiConfig.zip';
    link.click();
    URL.revokeObjectURL(url);
  };

  const getTitleText = () => {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Grid2 display={'flex'} flexDirection={'row'} gap={1}>
          <Typography variant={'h4'} component={'h1'}>
            Configuração da Bridge
          </Typography>
        </Grid2>

        <Typography variant={'body1'} component={'div'}>
          Para habilitar o recebimento de eventos gerados pelo AlertAI no
          Defense, siga os passos abaixo para configurar a Bridge:
        </Typography>
      </Box>
    );
  };

  const getStep1Text = () => {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant={'h5'} component={'div'}>
          Passo 1 - Adicionar uma nova Bridge
        </Typography>
        <Grid2 size={8} display="flex" flexDirection="column" gap={2}>
          <Typography
            display={'flex'}
            flexDirection={'row'}
            variant={'body1'}
            component={'div'}
          >
            Acesse
            <TextStepComponent steps={steps1} variant={'body1'} />,
          </Typography>
          <Box
            component="img"
            src={bridge_step1}
            alt="step1"
            sx={{
              width: '80%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
          <Typography
            display={'flex'}
            flexDirection={'row'}
            variant={'body1'}
            component={'div'}
          >
            E clique em <AddCircle sx={{ mx: 1 }} /> Adicionar.
          </Typography>
          <Box
            component="img"
            src={bridge_step2}
            alt="step1"
            sx={{
              width: '60%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
        </Grid2>
      </Box>
    );
  };

  const getStep2Text = () => {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant={'h5'} component={'div'}>
          Passo 2 - Configurar a conexão da Bridge
        </Typography>
        <Grid2 size={8} display="flex" flexDirection="column" gap={2}>
          <Typography
            display={'flex'}
            flexDirection={'row'}
            variant={'body1'}
            component={'div'}
          >
            Preencha os campos abaixo da seguinte forma no Defense:
          </Typography>
          <Box
            component="img"
            src={bridge_step3}
            alt="step1"
            sx={{
              width: '60%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
          <Typography
            display={'flex'}
            flexDirection={'row'}
            variant={'body1'}
            component={'div'}
            fontWeight={'bold'}
          >
            ATENÇÃO: A "Chave de Acesso" e a "Chave Secreta" não devem ser
            compartilhadas com ninguém e serão utilizadas para a configuração do
            Bridge no AlertAi.
          </Typography>
        </Grid2>
      </Box>
    );
  };

  const getStep3Text = () => {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant={'h5'} component={'div'}>
          Passo 3 - Configurar os tipos de evento
        </Typography>
        <Grid2 size={8} display="flex" flexDirection="column" gap={2}>
          <Grid2 display={'flex'} flexDirection={'row'} gap={1} size={12}>
            <Typography
              display={'flex'}
              flexDirection={'row'}
              variant={'body1'}
              component={'div'}
            >
              Faça o download do arquivo "AlertAiEventTypes" de configuração do
              AlertAi:
            </Typography>
          </Grid2>
          <ButtonComponent
            color={'secondary'}
            size={'small'}
            startIcon={<ArrowDownward />}
            onClick={() => downloadEventTypeAsync()}
          >
            AlertAiEventTypes.xlsx
          </ButtonComponent>
          <Typography
            display={'flex'}
            flexDirection={'row'}
            variant={'body1'}
            component={'div'}
          >
            E em seguida importe o arquivo de download para o defense de acordo
            com as imagens abaixo.
          </Typography>
          <Box
            component="img"
            src={bridge_step4}
            alt="step1"
            sx={{
              width: '80%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
          <Box
            component="img"
            src={bridge_step5}
            alt="step1"
            sx={{
              width: '60%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
        </Grid2>
      </Box>
    );
  };

  const getStep4Text = () => {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant={'h5'} component={'div'}>
          Passo 4 - Configurar os tipos de origem dos eventos
        </Typography>
        <Grid2 size={8} display="flex" flexDirection="column" gap={2}>
          <Grid2 display={'flex'} flexDirection={'row'} gap={1} size={12}>
            <Typography
              display={'flex'}
              flexDirection={'row'}
              variant={'body1'}
              component={'div'}
            >
              Faça o download do arquivo "AlertAiSourceEventTypes" de
              configuração do AlertAi:
            </Typography>
          </Grid2>
          <ButtonComponent
            size={'small'}
            color={'secondary'}
            startIcon={<ArrowDownward />}
            onClick={() => downloadSourceEventTypeAsync()}
          >
            AlertAiSourceEventTypes.xlsx
          </ButtonComponent>
          <Typography
            display={'flex'}
            flexDirection={'row'}
            variant={'body1'}
            component={'div'}
          >
            E em seguida importe o arquivo de download para o defense de acordo
            com as imagens abaixo.
          </Typography>
          <Box
            component="img"
            src={bridge_step6}
            alt="step1"
            sx={{
              width: '80%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
          <Box
            component="img"
            src={bridge_step5}
            alt="step1"
            sx={{
              width: '60%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
        </Grid2>
      </Box>
    );
  };

  const getStep5Text = () => {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant={'h5'} component={'div'}>
          Passo 5 - Integrar o Bridge do Defense no AlertAi
        </Typography>
        <Grid2 size={8} display="flex" flexDirection="column" gap={2}>
          <Grid2 display={'flex'} flexDirection={'row'} gap={1} size={12}>
            <Typography
              display={'flex'}
              flexDirection={'row'}
              variant={'body1'}
              component={'div'}
            >
              Configure a Bridge do Defense no AlertAi para concluir a
              integração:
            </Typography>
          </Grid2>
          <ButtonComponent
            color={'primary'}
            size={'small'}
            startIcon={<Settings />}
            onClick={handleBridgeConfigurationModal}
          >
            Configurar Bridge no AlertAi
          </ButtonComponent>
        </Grid2>
      </Box>
    );
  };

  const handleBridgeConfigurationModal = async () => {
    mutateGetByLicenseAsync(undefined, {
      onSuccess: setBridge,
    });
    setIsBridgeConfigModal(true);
  };

  const handleCreateOrUpdate = async (data: any, reset: () => void) => {
    if (data.id) {
      await mutateUpdateBridgeAsync(
        { data, id: data.id },
        {
          onSuccess: () => {
            showSnackbar(
              'Configuração da Bridge atualizada com sucesso.',
              'success',
            );
            setIsBridgeConfigModal(false);
            reset();
          },
        },
      );
      return;
    }

    await mutateCreateBridgeAsync(data, {
      onSuccess: () => {
        showSnackbar('Bridge configurada com sucesso.', 'success');
        setIsBridgeConfigModal(false);
        reset();
      },
    });
  };

  const handleDelete = async (id: number, reset: () => void) => {
    if (id) {
      await mutateDeleteBridgeAsync(id, {
        onSuccess: () => {
          showSnackbar(
            'Configuração da Bridge deletada com sucesso.',
            'success',
          );
          setIsBridgeConfigModal(false);
          reset();
        },
      });
    }
  };

  return (
    <>
      <Grid2 container>
        <Grid2
          size={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {getTitleText()}
          <Divider
            orientation={'horizontal'}
            sx={{ backgroundColor: '#fff' }}
          />
          {getStep1Text()}
          <Divider
            orientation={'horizontal'}
            sx={{ backgroundColor: '#fff' }}
          />
          {getStep2Text()}
          <Divider
            orientation={'horizontal'}
            sx={{ backgroundColor: '#fff' }}
          />
          {getStep3Text()}
          <Divider
            orientation={'horizontal'}
            sx={{ backgroundColor: '#fff' }}
          />
          {getStep4Text()}
          <Divider
            orientation={'horizontal'}
            sx={{ backgroundColor: '#fff' }}
          />
          {getStep5Text()}
        </Grid2>
      </Grid2>
      <DefenseBridgeConfigurationModalComponent
        isOpen={isBridgeConfigModal}
        setIsOpen={setIsBridgeConfigModal}
        handleCreateOrUpdate={handleCreateOrUpdate}
        handleDelete={handleDelete}
        bridge={bridge}
      />
    </>
  );
};

export default DefenseBridgePage;
